import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';

import {
  getGetSecret2QueryKey,
  getListSecretsQueryKey,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import { renameCredential } from '../credential-writes';
import { isCredentialNameConflict } from '../utils';

/**
 * Renames credentials, refreshing the lists that show them.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `rename`, which resolves to an error to show next to the name, or
 *   `undefined` once renamed.
 */
export function useRenameCredential(
  organizationId: string,
  workspaceId: string,
) {
  const { t } = useTranslation('credentials');
  const queryClient = useQueryClient();

  const rename = useCallback(
    async (
      credentialId: string,
      displayName: string,
    ): Promise<string | undefined> => {
      const name = displayName.trim();
      if (!name) return t('credentials.validation.displayNameRequired');
      try {
        await renameCredential(organizationId, workspaceId, credentialId, name);
      } catch (error) {
        if (isCredentialNameConflict(error)) {
          return t('credentials.validation.displayNameTaken');
        }
        captureException(error as Error, {
          tags: { module: 'credentials', action: 'renameCredential' },
        });
        return t('credentials.errors.updateFailed');
      }
      toast.success(t('credentials.connection.renamed'));
      void queryClient.invalidateQueries({
        queryKey: getListSecretsQueryKey(organizationId),
      });
      void queryClient.invalidateQueries({
        queryKey: getGetSecret2QueryKey(
          organizationId,
          workspaceId,
          credentialId,
        ),
      });
      return undefined;
    },
    [organizationId, workspaceId, queryClient, t],
  );

  return { rename };
}
